import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import Popup from "src/components/Popup/Popup";
import Toast, { type ToastState } from "src/components/Toast/Toast";
import { useAuth } from "src/context/AuthProvider";
import { usePollWhile } from "src/hooks/usePollWhile";
import {
    type FolderType,
    type SyncLogEntry,
    getHandle,
    setHandle,
    clearHandle,
    getFileState,
    setFileState,
    getSyncLog,
    appendSyncLogEntry,
    clearSyncLog,
} from "src/services/gdtSyncStorage";
import { uploadPadFile } from "src/services/padUploadService";
import { parseGdtFile, isMinorFromIso, type GdtParseError, type ParsedGdtPatient } from "src/services/gdtParser";
import { syncGdtPatient, cancelGdtSync, fetchRecentAgentCommit, type AgentCommitInfo, type GdtRecipientResolution, type GdtRevertInfo, type GdtSyncResult } from "src/services/gdtSyncService";
import { getPraxisSoftwareSyncSettings } from "src/services/agentSyncService";
import { apiRequest, ApiError } from "src/services/apiClient";
import { PraxisSoftwareSyncResultsPopup } from "src/components/PraxisSoftwareSyncResultsPopup/PraxisSoftwareSyncResultsPopup";
import { PraxisSoftwareSyncBlockingOverlay } from "src/components/PraxisSoftwareSyncBlockingOverlay/PraxisSoftwareSyncBlockingOverlay";
import { PraxisSoftwareSyncStatusBadge, type SyncStatusPhase } from "src/components/PraxisSoftwareSyncStatusBadge/PraxisSoftwareSyncStatusBadge";
import { GdtPatientReviewPopup } from "src/components/GdtPatientReviewPopup/GdtPatientReviewPopup";

export type { SyncLogEntry };

const POLL_INTERVAL_MS = 45_000;
const FOLDER_TYPES: FolderType[] = ["gdt", "dat"];

/** Which files in a connected folder this feature actually picks up. The "dat" folder
 *  holds invoice exports, which can be classic PAD (.dat/.pad) OR PADneXt — and
 *  PADneXt's real data file is XML, not .dat (backend/src/controllers/uploadController.ts
 *  already handles *_padx.xml uploads and explicitly rejects the companion *_auf.xml
 *  envelope/manifest file with ENVELOPE_FILE). Excluding *_auf.xml here up front avoids
 *  attempting it every single poll only to have the backend reject it every time — that
 *  would otherwise sit in the sync log as a permanent, never-resolving failure. */
function isAcceptedFile(type: FolderType, filename: string): boolean {
    const lower = filename.toLowerCase();
    // GDT exports are *.gdt or, in the standard's numbered variant, *.001 to *.999 (same rule as agent/src/watcher.ts).
    if (type === "gdt") return /\.(gdt|\d{3})$/.test(lower);
    if (lower.endsWith(".dat") || lower.endsWith(".pad")) return true;
    if (lower.endsWith(".xml")) return !lower.endsWith("_auf.xml");
    return false;
}

// "checking" is the initial value for both folders — reading the saved directory handle
// out of IndexedDB and asking it for permission (refreshConnectionState below) is async and
// takes a moment, so starting from "disconnected" made the tile flash a "Connect" button
// for an already-connected folder every page load, only to flip to "connected" a beat
// later. "checking" gets its own (spinner, no CTA) rendering in FolderTile instead.
/** How long a saved-folder lookup may take before it counts as failed (IndexedDB blocked by another tab, etc.). */
const CONNECTION_CHECK_TIMEOUT_MS = 5000;

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
    return new Promise<T>((resolve, reject) => {
        const timer = setTimeout(() => reject(new Error(`timed out after ${ms} ms`)), ms);
        promise.then(
            (v) => { clearTimeout(timer); resolve(v); },
            (e) => { clearTimeout(timer); reject(e); },
        );
    });
}

export type FolderConnectionState = "checking" | "disconnected" | "connected" | "needs-reconnect";

/** The file currently being processed, for a live progress readout. `progress` is a
 *  0-100 upload percentage for .dat files (real bytes-sent progress from the XHR
 *  upload); null for .gdt files, whose sync is a small one-shot JSON POST with no
 *  meaningful percentage to show. */
export interface CurrentSyncFile {
    folderType: FolderType;
    filename: string;
    progress: number | null;
}

/** A detected new-or-changed file awaiting staff confirmation before it's processed
 *  (only used when auto-parse is off). Holds the already-read File so confirming
 *  doesn't need to re-touch the filesystem. */
export interface PendingSyncFile {
    folderType: FolderType;
    filename: string;
    file: File;
    // True when this file's content (size + mtime) exactly matches what was already
    // successfully processed before — it's only here because a manual "Sync now" forces every
    // file back into consideration (so a record deleted from Medinvoice can be re-created), not
    // because anything about the file itself is new or changed. The confirm dialog needs this to
    // stop calling an unchanged, already-imported file "new".
    alreadyProcessed: boolean;
}

/** A .gdt file that's already been committed (patient created/updated the moment the file
 *  was parsed — see processOneFile) and is now awaiting the staff-facing review popup: QR
 *  code + an optional "send Einwilligung" action, plus (only when `result.needsRecipient`)
 *  picking/creating a billing recipient before a QR can exist at all. Queued one at a time
 *  so the popup shows a single patient at once, same shape as the dat-side results popup
 *  being one popup per run rather than per file. */
export interface PendingGdtReviewItem {
    folderType: "gdt";
    filename: string;
    file: File;
    parsed: ParsedGdtPatient;
    result: GdtSyncResult;
    /** The desktop agent already processed this record (§5b) — the popup shows a note. */
    agentCommit?: AgentCommitInfo;
}

/** Same shape as the /api/upload response's `summary` (see uploadController.ts) —
 *  mirrored here rather than imported since that's backend response JSON, not a
 *  shared type. Accumulated across every .dat/.xml file processed in one run, exactly
 *  like PadUploadPopup.tsx's manual-upload flow does. */
export interface DatUploadSummary {
    invoices: number;
    recipients: number;
    patients: number;
    samePersonInvoices: number;
    totalAmount: number;
    recipientsMissingEmail: number;
    emailsSent: number;
    emailsFailed: number;
    sendFailures: { readableId: string; recipientName: string; reason: string }[];
    duplicateUpload: { uploadedByName: string | null; uploadedAt: string; sameDoctor: boolean } | null;
}

function mergeDatSummary(prev: DatUploadSummary | null, s: DatUploadSummary): DatUploadSummary {
    if (!prev) return s;
    return {
        invoices: prev.invoices + s.invoices,
        recipients: prev.recipients + s.recipients,
        patients: prev.patients + s.patients,
        samePersonInvoices: prev.samePersonInvoices + s.samePersonInvoices,
        totalAmount: +(prev.totalAmount + s.totalAmount).toFixed(2),
        recipientsMissingEmail: prev.recipientsMissingEmail + s.recipientsMissingEmail,
        emailsSent: prev.emailsSent + s.emailsSent,
        emailsFailed: prev.emailsFailed + s.emailsFailed,
        sendFailures: [...prev.sendFailures, ...s.sendFailures],
        duplicateUpload: s.duplicateUpload ?? prev.duplicateUpload,
    };
}

export interface SyncFailure {
    folderType: FolderType;
    filename: string;
    message: string;
}

/** Everything that happened in one sync run — passed to PraxisSoftwareSyncResultsPopup
 *  once the run finishes. gdt outcomes aren't part of this: even though the patient write
 *  itself now happens synchronously (see processOneFile), the review popup remains its own
 *  dedicated surface for QR/consent, so gdt outcomes are only ever recorded in syncLog, not
 *  bundled into this end-of-run summary. */
export interface RunResults {
    dat: DatUploadSummary | null;
    failures: SyncFailure[];
}

function emptyRunResults(): RunResults {
    return { dat: null, failures: [] };
}

function hasContent(results: RunResults): boolean {
    return results.dat !== null || results.failures.length > 0;
}

interface PraxisSoftwareSyncContextValue {
    supported: boolean;
    connection: Record<FolderType, FolderConnectionState>;
    /** The connected folder's own name (e.g. "GDT-Export") — browsers never expose the
     *  full OS path for a picked directory, only its own name, so that's all there is
     *  to show. null when not connected. */
    folderNames: Record<FolderType, string | null>;
    connectFolder: (type: FolderType) => Promise<void>;
    reconnectFolder: (type: FolderType) => Promise<void>;
    disconnectFolder: (type: FolderType) => Promise<void>;
    syncing: boolean;
    currentFile: CurrentSyncFile | null;
    syncNow: () => Promise<void>;
    syncLog: SyncLogEntry[];
    clearSyncLog: () => Promise<void>;
    lastSyncAt: number | null;
    /** When true, new/changed files are processed immediately with no confirmation
     *  step. When false (default), they're queued in pendingConfirmation instead. */
    /** Praxis-wide policy (Settings → Praxis Software Sync), read-only from here. */
    autoParse: boolean;
    pendingConfirmation: PendingSyncFile[] | null;
    confirmPending: () => Promise<void>;
    declinePending: () => Promise<void>;
    /** Already-committed .gdt files awaiting the review popup — the front of the queue is
     *  what's shown. */
    pendingGdtReview: PendingGdtReviewItem[];
    /** Only used by the fallback recipient picker (item.result.needsRecipient) — attaches a
     *  staff-chosen recipient to the already-created patient. */
    resolveGdtReviewRecipient: (recipientResolution: GdtRecipientResolution) => Promise<GdtSyncResult | null>;
    sendGdtReviewConsent: (recipientId: string) => Promise<boolean>;
    /** Staff clicked Cancel in the review popup — undoes every write recorded for this file
     *  (the initial commit, plus a fallback-recipient resolution if one happened) and pops
     *  the item off the queue either way, so a cancel failure never leaves the popup stuck. */
    cancelGdtReview: (patientId: string, revertHistory: GdtRevertInfo[]) => Promise<void>;
    advanceGdtReview: () => void;
}

const PraxisSoftwareSyncContext = createContext<PraxisSoftwareSyncContextValue | null>(null);

// A single shared instance (not one per page) — mounted once near the app shell (see
// App.tsx) so the poll loop keeps running while any authenticated tab is open, and the
// Settings page just observes/controls that same instance instead of racing a second
// poll loop of its own (which would risk double-uploading a changed .dat file).
//
// Named generically (not after any one PVS product) — GDT and PAD/PADneXt are standard
// German practice-management export formats used by more than one vendor's software;
// nothing here is tied to a specific product. The exact GDT field layout can still vary
// by vendor, which is why gdtParser.ts flags its field dictionary as needing
// per-installation confirmation rather than assuming one fixed spec covers everyone.
export function PraxisSoftwareSyncProvider({ children }: { children: ReactNode }) {
    const { t } = useTranslation();
    const { user } = useAuth();
    const isAuthenticated = !!user;
    const supported = typeof window !== "undefined" && "showDirectoryPicker" in window;

    const [connection, setConnection] = useState<Record<FolderType, FolderConnectionState>>({
        gdt: "checking",
        dat: "checking",
    });
    const [folderNames, setFolderNames] = useState<Record<FolderType, string | null>>({
        gdt: null,
        dat: null,
    });
    const [syncing, setSyncing] = useState(false);
    // Separate from `syncing`: the silent background poll (every 45s, whenever a folder
    // is connected) also sets `syncing` true for the few hundred ms it takes to list
    // both folders and diff file state, even when it finds nothing — that's supposed to
    // be invisible. Only a manual "Sync now" click or working through a confirmed batch
    // is a foreground action the blocking overlay should cover; gating the overlay on
    // `syncing` directly made it flash on every poll tick instead.
    const [blockingSync, setBlockingSync] = useState(false);
    const [currentFile, setCurrentFile] = useState<CurrentSyncFile | null>(null);
    const [syncLog, setSyncLog] = useState<SyncLogEntry[]>([]);
    const [lastSyncAt, setLastSyncAt] = useState<number | null>(null);
    const [syncResults, setSyncResults] = useState<RunResults | null>(null);
    // Only used for the manual "Sync now" button — a poll-triggered run stays silent
    // when there's nothing to do, but a deliberate click must always show *something*,
    // even when the answer is "nothing changed" (see the force branch in runSync).
    const [toast, setToast] = useState<ToastState | null>(null);
    // Set for the full duration of a multi-file batch (auto-parse run or a confirmed
    // batch) — drives the blocking overlay's "file X of N" readout and overall bar.
    const [batchProgress, setBatchProgress] = useState<{ index: number; total: number } | null>(null);
    // Drives the persistent bottom-right status badge — unlike the blocking overlay
    // (foreground only), this also covers a silent background auto-parse run, since the
    // whole point staff raised is "how do I know when it's actually safe to trust what
    // I see elsewhere in the app", not just "don't let me navigate away mid-batch".
    const [statusPhase, setStatusPhase] = useState<SyncStatusPhase | null>(null);
    const [autoParse, setAutoParseState] = useState(false);
    const [pendingConfirmation, setPendingConfirmation] = useState<PendingSyncFile[] | null>(null);
    const [pendingGdtReview, setPendingGdtReview] = useState<PendingGdtReviewItem[]>([]);

    const handlesRef = useRef<Partial<Record<FolderType, FileSystemDirectoryHandle>>>({});
    // Guards against a poll and a confirm/manual sync running at the same time and
    // racing over the same files (e.g. double-uploading a .dat while it's mid-confirm).
    const busyRef = useRef(false);

    // Which login a run belongs to. Logging out (or another user/praxis logging in) changes it, and
    // everything found for the previous session is dropped: polling stops on logout, but popups
    // already queued — "new files detected", the patient review, results — used to stay on screen
    // over the login page, and a run still in flight opened its popup after the logout.
    const sessionKey = user ? `${user.praxis?.id ?? ""}:${user.doctor?.id ?? ""}` : "";
    const sessionKeyRef = useRef(sessionKey);
    sessionKeyRef.current = sessionKey;
    useEffect(() => {
        // The folders too: refreshConnectionState re-reads them for the new session. Kept, the
        // previous session's "connected" state started the new session's first poll on its handles
        // before that check had even run.
        handlesRef.current = {};
        setConnection({ gdt: "checking", dat: "checking" });
        setFolderNames({ gdt: null, dat: null });
        setPendingConfirmation(null);
        setPendingGdtReview([]);
        setSyncResults(null);
        setToast(null);
        setStatusPhase(null);
        setBlockingSync(false);
        setBatchProgress(null);
        setCurrentFile(null);
    }, [sessionKey]);

    const appendLog = useCallback((entry: Omit<SyncLogEntry, "id" | "timestamp">) => {
        const full: SyncLogEntry = { ...entry, id: `${Date.now()}-${Math.random()}`, timestamp: Date.now() };
        setSyncLog((prev) => [full, ...prev].slice(0, 200));
        // Fire-and-forget — a log-persist failure shouldn't block/fail the sync itself,
        // and the entry is already visible via React state either way this session.
        void appendSyncLogEntry(full);
    }, []);

    const clearLog = useCallback(async () => {
        setSyncLog([]);
        await clearSyncLog();
    }, []);

    const refreshConnectionState = useCallback(async () => {
        if (!isAuthenticated) return;
        const next: Record<FolderType, FolderConnectionState> = { gdt: "disconnected", dat: "disconnected" };
        const nextNames: Record<FolderType, string | null> = { gdt: null, dat: null };
        // Every path below must end in setConnection: the state starts as "checking" (the spinner), so a
        // routine that returns early or throws would leave it on "checking" forever. That is exactly what
        // used to happen in a browser without folder access (early return) and whenever IndexedDB or a saved
        // folder handle failed or hung (unhandled rejection). A failed lookup now reads as "not connected" —
        // the user can simply connect the folder again — instead of an endless spinner.
        if (supported) {
            for (const type of FOLDER_TYPES) {
                try {
                    const handle = await withTimeout(getHandle(type), CONNECTION_CHECK_TIMEOUT_MS);
                    if (!handle) continue;
                    const state = await withTimeout(handle.queryPermission({ mode: "read" }), CONNECTION_CHECK_TIMEOUT_MS);
                    // Only a handle whose permission could actually be queried is kept for polling.
                    handlesRef.current[type] = handle;
                    nextNames[type] = handle.name;
                    next[type] = state === "granted" ? "connected" : "needs-reconnect";
                } catch (err) {
                    console.warn(`[praxisSoftwareSync] could not check the ${type} folder — treating it as not connected:`, err);
                    delete handlesRef.current[type];
                    next[type] = "disconnected";
                    nextNames[type] = null;
                }
            }
        }
        setConnection(next);
        setFolderNames(nextNames);
    }, [supported, isAuthenticated]);

    useEffect(() => {
        void refreshConnectionState();
        // sessionKey: the reset above sets "checking" on every login change, so the check must run
        // again even when isAuthenticated stays true (one login replacing another).
    }, [refreshConnectionState, isAuthenticated, sessionKey]);

    useEffect(() => {
        if (!supported || !isAuthenticated) return;
        // Merge rather than replace — a sync could already have appended entries to
        // React state (and IndexedDB) by the time this load resolves; don't drop them.
        getSyncLog()
            .then((loaded) => {
                setSyncLog((prev) => {
                    const merged = [...prev, ...loaded.filter((l) => !prev.some((p) => p.id === l.id))];
                    return merged.sort((a, b) => b.timestamp - a.timestamp).slice(0, 200);
                });
            })
            .catch(() => {});
    }, [supported, isAuthenticated]);

    // Praxis-wide policy now (Settings → Praxis Software Sync), not a per-browser preference —
    // read-only here. Refetched whenever this provider (re)mounts for a signed-in user; a change
    // made in another tab only takes effect on this tab's next reload, same as every other setting
    // this app reads once per session.
    useEffect(() => {
        if (!supported || !isAuthenticated) return;
        getPraxisSoftwareSyncSettings().then((s) => setAutoParseState(s.autoParse)).catch(() => {});
    }, [supported, isAuthenticated]);

    useEffect(() => {
        if (batchProgress) {
            setStatusPhase("syncing");
            return;
        }
        setStatusPhase((prev) => (prev === "syncing" ? "done" : prev));
    }, [batchProgress]);

    useEffect(() => {
        if (statusPhase !== "done") return;
        const timer = setTimeout(() => setStatusPhase(null), 3000);
        return () => clearTimeout(timer);
    }, [statusPhase]);

    const connectFolder = useCallback(async (type: FolderType) => {
        if (!supported || !window.showDirectoryPicker) return;
        const handle = await window.showDirectoryPicker({ id: `medinvoice-${type}`, mode: "read" });
        await setHandle(type, handle);
        handlesRef.current[type] = handle;
        setConnection((prev) => ({ ...prev, [type]: "connected" }));
        setFolderNames((prev) => ({ ...prev, [type]: handle.name }));
    }, [supported]);

    const reconnectFolder = useCallback(async (type: FolderType) => {
        const handle = handlesRef.current[type] ?? (await getHandle(type));
        if (!handle) return;
        const state = await handle.requestPermission({ mode: "read" });
        setConnection((prev) => ({ ...prev, [type]: state === "granted" ? "connected" : "needs-reconnect" }));
        setFolderNames((prev) => ({ ...prev, [type]: handle.name }));
    }, []);

    const disconnectFolder = useCallback(async (type: FolderType) => {
        await clearHandle(type);
        delete handlesRef.current[type];
        setConnection((prev) => ({ ...prev, [type]: "disconnected" }));
        setFolderNames((prev) => ({ ...prev, [type]: null }));
    }, []);

    const processDatFile = useCallback(async (file: File, onProgress: (pct: number) => void): Promise<{ summary: DatUploadSummary | null }> => {
        const res = await uploadPadFile(file, onProgress);
        if (res?.error) {
            throw new Error(res.message || res.error);
        }
        const s = res?.summary;
        if (!s) return { summary: null };
        return {
            summary: {
                invoices: s.invoices ?? 0,
                recipients: s.recipients ?? 0,
                patients: s.patients ?? 0,
                samePersonInvoices: s.samePersonInvoices ?? 0,
                totalAmount: s.totalAmount ?? 0,
                recipientsMissingEmail: s.recipientsMissingEmail ?? 0,
                emailsSent: s.emailsSent ?? 0,
                emailsFailed: s.emailsFailed ?? 0,
                sendFailures: s.sendFailures ?? [],
                duplicateUpload: s.duplicateUpload ?? null,
            },
        };
    }, []);

    /** Processes exactly one already-detected file (auto path or a confirmed pending one).
     *  For .dat: upload, persist fileState, log the outcome, fold into the run accumulator.
     *  For .gdt: parse, then commit immediately — a minor with a guardian name on file
     *  (FK 3201/3202) gets that guardian auto-created as the recipient; everyone else falls
     *  through to upsertGdtPatient's own default (keep an existing link, self-derive for a
     *  non-minor, or leave needsRecipient for a minor with nothing to derive from) by
     *  sending no explicit resolution at all. Either way the write (and its log entry)
     *  happens right here — the review popup that's queued afterward only ever shows a QR
     *  and an optional "send Einwilligung" action (plus, when needsRecipient, the one-time
     *  fallback picker), never gates the write itself. */
    const processOneFile = useCallback(async (
        type: FolderType,
        filename: string,
        file: File,
        results: RunResults,
    ) => {
        const startedIn = sessionKeyRef.current;
        setCurrentFile({ folderType: type, filename, progress: type === "dat" ? 0 : null });
        try {
            if (type === "dat") {
                const { summary } = await processDatFile(file, (pct) => setCurrentFile((prev) =>
                    prev && prev.filename === filename ? { ...prev, progress: pct } : prev,
                ));
                let message: string;
                if (summary) {
                    results.dat = mergeDatSummary(results.dat, summary);
                    message = t("praxisSoftwareSync.logInvoicesUploaded", { count: summary.invoices });
                } else {
                    message = t("praxisSoftwareSync.logUploaded");
                }
                await setFileState(type, filename, {
                    lastModified: file.lastModified,
                    size: file.size,
                    status: "processed",
                });
                appendLog({ folderType: type, filename, status: "success", message });
            } else {
                const parsed = await parseGdtFile(file); // throws GdtParseError on bad content
                // Same as the desktop app: the whole guardian block, and the file's email/phone
                // (a minor's record carries the parent's there). matchExisting links a newborn to
                // the parent already on file instead of creating a second one.
                const guardianResolution: GdtRecipientResolution | undefined =
                    isMinorFromIso(parsed.birthDate) && parsed.guardian
                        ? {
                            mode: "new",
                            matchExisting: true,
                            newRecipient: { ...parsed.guardian, email: parsed.email, tel: parsed.tel },
                        }
                        : undefined;
                const result = await syncGdtPatient(parsed, guardianResolution);
                const message = result.needsRecipient
                    ? t("praxisSoftwareSync.logPatientCreatedNeedsRecipient")
                    : result.created
                        ? t("praxisSoftwareSync.logPatientCreated")
                        : t("praxisSoftwareSync.logPatientUpdated");
                await setFileState(type, filename, {
                    lastModified: file.lastModified,
                    size: file.size,
                    status: "processed",
                });
                appendLog({ folderType: type, filename, status: "success", message });
                const agentCommit = await fetchRecentAgentCommit(parsed.externalId);
                if (sessionKeyRef.current !== startedIn) return; // no review popup for a logged-out session
                setPendingGdtReview((prev) => [...prev, { folderType: type, filename, file, parsed, result, ...(agentCommit ? { agentCommit } : {}) }]);
            }
        } catch (err: any) {
            const message = (err as GdtParseError)?.message
                ?? (err instanceof ApiError ? err.message : t("praxisSoftwareSync.unknownError"));
            results.failures.push({ folderType: type, filename, message });
            // Deliberately NOT calling setFileState here — leaving the file's state
            // stale/absent means the next poll retries it, per the "a failed file
            // should never silently disappear" requirement.
            appendLog({ folderType: type, filename, status: "error", message });
        } finally {
            setCurrentFile(null);
        }
    }, [processDatFile, appendLog, t]);

    const showResultsIfAny = useCallback((results: RunResults) => {
        if (hasContent(results)) setSyncResults(results);
    }, []);

    const runSync = useCallback(async (options?: { force?: boolean }) => {
        const force = options?.force ?? false;
        if (!supported || !isAuthenticated) return;
        if (busyRef.current) {
            // A background poll (or an already-running manual sync) is mid-flight. Silently
            // dropping the click here was the actual cause of "Sync now" sometimes doing
            // nothing visible — a manual click must never be a no-op with zero feedback.
            if (force) setToast({ message: t("praxisSoftwareSync.syncAlreadyRunning"), variant: "success" });
            return;
        }
        busyRef.current = true;
        const startedIn = sessionKeyRef.current;
        setSyncing(true);
        // Only a manual "Sync now" click (force: true) is a foreground action — a
        // background poll runs this same function silently, so it must not trip the
        // blocking overlay just because it's collecting/diffing folder listings.
        if (force) setBlockingSync(true);
        const results = emptyRunResults();
        // Collected up front, across both folders, before anything is processed — the
        // per-file loop used to process-as-it-walked, so the batch's true size wasn't
        // known until it was already done, and the on-page progress readout stopped
        // being visible (and everything stayed interactive) the moment staff navigated
        // away mid-run. Knowing the full batch size lets the blocking overlay below show
        // real "file X of N" progress for the whole run, not just the file in flight.
        const candidates: PendingSyncFile[] = [];
        let folderError = false;
        try {
            for (const type of FOLDER_TYPES) {
                const handle = handlesRef.current[type];
                if (!handle) continue;

                // Each folder is handled in its own try/catch — a renamed/moved/deleted folder
                // (or one of its parents) makes the handle permanently stale, and queryPermission/
                // values() throws (typically NotFoundError) instead of just reporting "not granted".
                // That used to escape this whole function uncaught, which silently abandoned the
                // OTHER folder type too (e.g. a renamed Patient Data folder meant Invoice Data was
                // never even looked at) and left the UI with no feedback at all — just a flicker.
                try {
                    const permission = await handle.queryPermission({ mode: "read" });
                    if (permission !== "granted") {
                        setConnection((prev) => ({ ...prev, [type]: "needs-reconnect" }));
                        continue;
                    }

                    const fileState = await getFileState(type);

                    for await (const entry of handle.values()) {
                        if (entry.kind !== "file") continue;
                        if (!isAcceptedFile(type, entry.name)) continue;

                        const fileHandle = entry as unknown as FileSystemFileHandle;
                        const file = await fileHandle.getFile();
                        const known = fileState[entry.name];
                        // force (manual "Sync now") ignores this cache entirely — the file's
                        // content can be unchanged while the record it produced was since
                        // deleted in Medinvoice, and a forced sync should re-create it, not
                        // trust the "already processed" cache from before the deletion.
                        const contentUnchanged = !!known && known.lastModified === file.lastModified && known.size === file.size;
                        if (!force && contentUnchanged) continue;

                        candidates.push({ folderType: type, filename: entry.name, file, alreadyProcessed: force && contentUnchanged });
                    }
                } catch (err) {
                    // Same conclusion refreshConnectionState already draws for this exact failure:
                    // "disconnected", not "needs-reconnect" — reconnectFolder only re-requests
                    // permission on this same dead handle, which can't fix a folder that no longer
                    // exists at that path. "disconnected" surfaces a "Connect" button, which opens
                    // a fresh picker.
                    console.warn(`[praxisSoftwareSync] could not read the ${type} folder during sync — treating it as not connected:`, err);
                    delete handlesRef.current[type];
                    setConnection((prev) => ({ ...prev, [type]: "disconnected" }));
                    setFolderNames((prev) => ({ ...prev, [type]: null }));
                    folderError = true;
                    // Shown regardless of force: this is new information about the staff's own
                    // setup (the folder just vanished out from under a connected handle), and it
                    // only fires once — handlesRef is cleared above, so the NEXT tick (background
                    // poll or another click) just sees "no handle" and skips quietly, same as a
                    // folder that was never connected in the first place.
                    setToast({
                        message: t("praxisSoftwareSync.folderNotFound", {
                            folder: t(type === "gdt" ? "praxisSoftwareSync.gdtFolder" : "praxisSoftwareSync.datFolder"),
                        }),
                        variant: "error",
                    });
                }
            }

            if (autoParse && candidates.length > 0) {
                for (let i = 0; i < candidates.length; i++) {
                    if (sessionKeyRef.current !== startedIn) break; // logged out mid-batch
                    setBatchProgress({ index: i, total: candidates.length });
                    const c = candidates[i];
                    await processOneFile(c.folderType, c.filename, c.file, results);
                }
            }
        } finally {
            busyRef.current = false;
            setSyncing(false);
            setBlockingSync(false);
            setBatchProgress(null);
            setLastSyncAt(Date.now());
            // Logged out (or someone else logged in) while this run was going — show nothing.
            if (sessionKeyRef.current !== startedIn) return;
            showResultsIfAny(results);
            if (!autoParse && candidates.length > 0) {
                setPendingConfirmation((prev) => {
                    const key = (f: PendingSyncFile) => `${f.folderType}:${f.filename}`;
                    const existingKeys = new Set((prev ?? []).map(key));
                    return [...(prev ?? []), ...candidates.filter((f) => !existingKeys.has(key(f)))];
                });
            } else if (force && !hasContent(results) && !folderError) {
                // Manual click, nothing new/changed found (e.g. re-clicking right after
                // declining, with no new file dropped in the meantime) — say so instead
                // of leaving the button's only feedback be a timestamp quietly updating.
                // Skipped when a folder error toast was just set above — that one must win.
                setToast({ message: t("praxisSoftwareSync.nothingToSync"), variant: "success" });
            }
        }
    }, [supported, isAuthenticated, autoParse, processOneFile, showResultsIfAny, t]);

    const confirmPending = useCallback(async () => {
        const batch = pendingConfirmation;
        if (!batch || batch.length === 0 || busyRef.current) return;
        setPendingConfirmation(null);
        busyRef.current = true;
        const startedIn = sessionKeyRef.current;
        setSyncing(true);
        setBlockingSync(true); // always an explicit staff action — never triggered silently
        const results = emptyRunResults();
        try {
            for (let i = 0; i < batch.length; i++) {
                if (sessionKeyRef.current !== startedIn) break; // logged out mid-batch
                setBatchProgress({ index: i, total: batch.length });
                const item = batch[i];
                await processOneFile(item.folderType, item.filename, item.file, results);
            }
        } finally {
            busyRef.current = false;
            setSyncing(false);
            setBlockingSync(false);
            setBatchProgress(null);
            setLastSyncAt(Date.now());
            if (sessionKeyRef.current === startedIn) showResultsIfAny(results);
        }
    }, [pendingConfirmation, processOneFile, showResultsIfAny]);

    const declinePending = useCallback(async () => {
        const batch = pendingConfirmation;
        if (!batch) return;
        setPendingConfirmation(null);
        for (const item of batch) {
            await setFileState(item.folderType, item.filename, {
                lastModified: item.file.lastModified,
                size: item.file.size,
                status: "skipped",
            });
            appendLog({
                folderType: item.folderType,
                filename: item.filename,
                status: "skipped",
                message: t("praxisSoftwareSync.logSkipped"),
            });
        }
    }, [pendingConfirmation, appendLog, t]);

    /** Only reached when the front item's result.needsRecipient is true — staff picked or
     *  created a recipient in the popup's fallback step, attach it to the patient that was
     *  already committed in processOneFile. Safe to call again: findExistingGdtPatient will
     *  find that same patient by externalId, so this is an update, not a second create. */
    const resolveGdtReviewRecipient = useCallback(async (
        recipientResolution: GdtRecipientResolution,
    ): Promise<GdtSyncResult | null> => {
        const item = pendingGdtReview[0];
        if (!item) return null;
        try {
            const result = await syncGdtPatient(item.parsed, recipientResolution);
            appendLog({
                folderType: item.folderType,
                filename: item.filename,
                status: "success",
                message: t("praxisSoftwareSync.logRecipientAssigned"),
            });
            return result;
        } catch (err: any) {
            const message = err instanceof ApiError ? err.message : t("praxisSoftwareSync.unknownError");
            appendLog({ folderType: item.folderType, filename: item.filename, status: "error", message });
            return null;
        }
    }, [pendingGdtReview, appendLog, t]);

    /** Staff clicked "… and send Einwilligung" in the review popup — a failure here is
     *  logged as its own outcome but never undoes the already-successful patient save. */
    const sendGdtReviewConsent = useCallback(async (recipientId: string): Promise<boolean> => {
        const item = pendingGdtReview[0];
        if (!item) return false;
        try {
            await apiRequest(
                "/patient-portal/questionnaire/create-link",
                { method: "POST", body: JSON.stringify({ recipientId }) },
                { "Content-Type": "application/json" },
            );
            appendLog({ folderType: item.folderType, filename: item.filename, status: "success", message: t("praxisSoftwareSync.logConsentSent") });
            return true;
        } catch (err: any) {
            const reason = err instanceof ApiError ? err.message : t("praxisSoftwareSync.unknownError");
            appendLog({ folderType: item.folderType, filename: item.filename, status: "error", message: t("praxisSoftwareSync.logConsentFailed", { reason }) });
            return false;
        }
    }, [pendingGdtReview, appendLog, t]);

    /** Staff clicked Cancel in the review popup — reverts revertHistory in reverse (most
     *  recent write first), so a fallback-recipient resolution's own revert runs before the
     *  original create/update's, matching the order the writes actually happened in. Always
     *  pops the queue afterward, even on failure — a stuck popup over a partially-failed
     *  revert would be worse than a logged error the doctor can chase up in the sync log. */
    const cancelGdtReview = useCallback(async (patientId: string, revertHistory: GdtRevertInfo[]) => {
        const item = pendingGdtReview[0];
        if (!item) return;
        try {
            for (let i = revertHistory.length - 1; i >= 0; i--) {
                await cancelGdtSync(patientId, revertHistory[i]);
            }
            appendLog({ folderType: item.folderType, filename: item.filename, status: "cancelled", message: t("praxisSoftwareSync.logImportCancelled") });
        } catch (err: any) {
            const reason = err instanceof ApiError ? err.message : t("praxisSoftwareSync.unknownError");
            appendLog({ folderType: item.folderType, filename: item.filename, status: "error", message: t("praxisSoftwareSync.logCancelFailed", { reason }) });
        } finally {
            setPendingGdtReview((prev) => prev.slice(1));
        }
    }, [pendingGdtReview, appendLog, t]);

    /** Pops the front of the review queue — called once the popup is done with that item
     *  (Add/Save, Add+consent, or the X close, all of which are just dismissals now that
     *  the write already happened in processOneFile). */
    const advanceGdtReview = useCallback(() => {
        setPendingGdtReview((prev) => prev.slice(1));
    }, []);

    const anyConnected = connection.gdt === "connected" || connection.dat === "connected";
    usePollWhile(supported && isAuthenticated && anyConnected, runSync, POLL_INTERVAL_MS);

    // The manual button always forces a real re-check — see the `force` comment in
    // runSync for why the background poll can't just do this on every tick too.
    const syncNow = useCallback(() => runSync({ force: true }), [runSync]);

    return (
        <PraxisSoftwareSyncContext.Provider
            value={{
                supported, connection, folderNames, connectFolder, reconnectFolder, disconnectFolder,
                syncing, currentFile, syncNow, syncLog, clearSyncLog: clearLog, lastSyncAt,
                autoParse, pendingConfirmation, confirmPending, declinePending,
                pendingGdtReview, resolveGdtReviewRecipient, sendGdtReviewConsent, cancelGdtReview, advanceGdtReview,
            }}
        >
            {children}
            {/* Nothing below ever shows without a logged-in user — see sessionKey above. */}
            {isAuthenticated && <>
            {blockingSync && <PraxisSoftwareSyncBlockingOverlay currentFile={currentFile} batchProgress={batchProgress} />}
            {statusPhase && (
                <PraxisSoftwareSyncStatusBadge phase={statusPhase} currentFile={currentFile} batchProgress={batchProgress} />
            )}
            {toast && <Toast message={toast.message} variant={toast.variant} onClose={() => setToast(null)} />}
            {syncResults && <PraxisSoftwareSyncResultsPopup results={syncResults} onClose={() => setSyncResults(null)} />}
            {pendingConfirmation && pendingConfirmation.length > 0 && (
                <Popup
                    title={t(pendingConfirmation.every((f) => f.alreadyProcessed)
                        ? "praxisSoftwareSync.confirmTitleRecheck"
                        : "praxisSoftwareSync.confirmTitle")}
                    onClose={() => void declinePending()}
                    message={
                        <div>
                            <p>{t(pendingConfirmation.every((f) => f.alreadyProcessed)
                                ? "praxisSoftwareSync.confirmIntroRecheck"
                                : "praxisSoftwareSync.confirmIntro", { count: pendingConfirmation.length })}</p>
                            <div className="pvs-sync-confirm-breakdown">
                                {pendingConfirmation.filter((f) => f.folderType === "gdt").length > 0 && (
                                    <span className="pvs-sync-confirm-breakdown__item">
                                        {t("praxisSoftwareSync.confirmBreakdownPatients", {
                                            count: pendingConfirmation.filter((f) => f.folderType === "gdt").length,
                                        })}
                                    </span>
                                )}
                                {pendingConfirmation.filter((f) => f.folderType === "dat").length > 0 && (
                                    <span className="pvs-sync-confirm-breakdown__item">
                                        {t("praxisSoftwareSync.confirmBreakdownInvoices", {
                                            count: pendingConfirmation.filter((f) => f.folderType === "dat").length,
                                        })}
                                    </span>
                                )}
                            </div>
                            <ul className="pvs-sync-confirm-list">
                                {pendingConfirmation.map((f) => (
                                    <li key={`${f.folderType}:${f.filename}`}>
                                        <span className={`pvs-sync-confirm-list__type pvs-sync-confirm-list__type--${f.folderType}`}>
                                            {f.folderType === "gdt" ? t("praxisSoftwareSync.gdtFolderShort") : t("praxisSoftwareSync.datFolderShort")}
                                        </span>
                                        {f.alreadyProcessed && (
                                            <span className="pvs-sync-confirm-list__already" title={t("praxisSoftwareSync.confirmAlreadyProcessedHint")}>
                                                {t("praxisSoftwareSync.confirmAlreadyProcessed")}
                                            </span>
                                        )}
                                        {f.filename}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    }
                    actions={[
                        { label: t("praxisSoftwareSync.confirmDecline"), variant: "default", onClick: () => void declinePending() },
                        { label: t("praxisSoftwareSync.confirmStart"), variant: "primary", onClick: () => void confirmPending() },
                    ]}
                />
            )}
            {pendingGdtReview.length > 0 && (
                <GdtPatientReviewPopup
                    key={`${pendingGdtReview[0].folderType}:${pendingGdtReview[0].filename}`}
                    item={pendingGdtReview[0]}
                    onResolveRecipient={resolveGdtReviewRecipient}
                    onSendConsent={sendGdtReviewConsent}
                    onCancel={cancelGdtReview}
                    onAdvance={advanceGdtReview}
                />
            )}
            </>}
        </PraxisSoftwareSyncContext.Provider>
    );
}

export function usePraxisSoftwareSync(): PraxisSoftwareSyncContextValue {
    const ctx = useContext(PraxisSoftwareSyncContext);
    if (!ctx) throw new Error("usePraxisSoftwareSync must be used within a PraxisSoftwareSyncProvider");
    return ctx;
}
